/**
 * Brand exploration sheet — internal review only.
 * Visit `/brand` on the landing app to see all logomark + wordmark variants.
 */
import {
  Brand,
  Logomark,
  Mark3x3,
  Mark4x4,
  Wordmark,
} from '../components/brand';

const ACCENT_POSITIONS: Array<{ label: string; pos: { row: number; col: number } }> = [
  { label: 'r2 · c1 (active row, default)', pos: { row: 2, col: 1 } },
  { label: 'r0 · c0 (top-left, "fresh")', pos: { row: 0, col: 0 } },
  { label: 'r1 · c2 (centre-ish)', pos: { row: 1, col: 2 } },
  { label: 'r3 · c3 (bottom-right, "complete")', pos: { row: 3, col: 3 } },
  { label: 'r0 · c3 (top-right)', pos: { row: 0, col: 3 } },
  { label: 'r3 · c0 (bottom-left)', pos: { row: 3, col: 0 } },
];

const WORDMARK_VARIANTS: Array<{
  id: string;
  label: string;
  props: Parameters<typeof Wordmark>[0];
}> = [
  { id: 'lc-medium', label: 'lowercase · Medium 500 · tracking -0.035em', props: { size: 32, weight: 'medium', case: 'lower' } },
  { id: 'lc-regular', label: 'lowercase · Regular 400', props: { size: 32, weight: 'regular', case: 'lower' } },
  { id: 'lc-semibold', label: 'lowercase · Semibold 600', props: { size: 32, weight: 'semibold', case: 'lower' } },
  { id: 'lc-bold', label: 'lowercase · Bold 700', props: { size: 32, weight: 'bold', case: 'lower' } },
  { id: 'sentence-medium', label: 'Sentence · Medium 500', props: { size: 32, weight: 'medium', case: 'sentence' } },
  { id: 'upper-medium', label: 'UPPERCASE · Medium 500 · tracking 0.06em', props: { size: 22, weight: 'medium', case: 'upper', tracking: 0.06 } },
  { id: 'lc-medium-tight', label: 'lowercase · Medium · tighter (-0.06em)', props: { size: 32, weight: 'medium', case: 'lower', tracking: -0.06 } },
  { id: 'lc-medium-ligature', label: 'lowercase · Medium · emerald `up` ligature', props: { size: 32, weight: 'medium', case: 'lower', ligature: true } },
  { id: 'lc-bold-ligature', label: 'lowercase · Bold · emerald `up` ligature', props: { size: 32, weight: 'bold', case: 'lower', ligature: true } },
];

function Pane({
  mode,
  children,
  label,
}: {
  mode: 'light' | 'dark';
  children: React.ReactNode;
  label: string;
}) {
  return (
    <div
      className={mode === 'dark' ? 'dark' : ''}
      style={{
        background: mode === 'dark' ? '#0b0b0d' : '#ffffff',
        color: mode === 'dark' ? '#e8e8ea' : '#111114',
        borderRadius: 16,
        border: '1px solid var(--border-line)',
        padding: 28,
      }}
    >
      <div
        style={{
          fontSize: 11,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          opacity: 0.5,
          marginBottom: 16,
          fontFamily: 'Geist Mono, monospace',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function Card({ children, label }: { children: React.ReactNode; label?: string }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-hair)',
        borderRadius: 12,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        minHeight: 140,
      }}
    >
      {children}
      {label && (
        <div
          style={{
            fontSize: 10.5,
            fontFamily: 'Geist Mono, monospace',
            color: 'var(--fg-low)',
            textAlign: 'center',
            letterSpacing: '0.02em',
            marginTop: 4,
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

function H2({ children }: { children: React.ReactNode }) {
  return (
    <h2
      style={{
        fontSize: 13,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--fg-low)',
        margin: '64px 0 20px',
        fontFamily: 'Geist Mono, monospace',
      }}
    >
      {children}
    </h2>
  );
}

export function BrandSheet() {
  return (
    <div
      style={{
        maxWidth: 1280,
        margin: '0 auto',
        padding: '48px 24px 120px',
        color: 'var(--fg-high)',
        background: 'var(--bg-canvas)',
        minHeight: '100vh',
      }}
    >
      <div style={{ marginBottom: 12 }}>
        <Brand size={36} weight="medium" href={null} />
      </div>
      <h1
        style={{
          fontSize: 40,
          fontWeight: 600,
          letterSpacing: '-0.03em',
          margin: '8px 0 8px',
          fontFamily: 'Geist, sans-serif',
        }}
      >
        Brand explorations
      </h1>
      <p style={{ color: 'var(--fg-low)', fontSize: 14, maxWidth: 640 }}>
        Yantra-grid logomark family + wordmark variations. Internal review sheet;
        not linked from the public navigation. Lock the picks and we'll remove
        this page from the build.
      </p>

      {/* ──────────────────────────────────────────────────────────────── */}
      <H2>1 · Logomark — canonical lockup (light + dark)</H2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Pane mode="light" label="Light surface">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28, alignItems: 'flex-start' }}>
            <Brand size={64} weight="medium" href={null} />
            <Brand size={40} weight="medium" href={null} />
            <Brand size={28} weight="medium" href={null} />
            <Brand size={20} weight="medium" href={null} />
          </div>
        </Pane>
        <Pane mode="dark" label="Dark surface">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 28, alignItems: 'flex-start' }}>
            <Brand size={64} weight="medium" href={null} />
            <Brand size={40} weight="medium" href={null} />
            <Brand size={28} weight="medium" href={null} />
            <Brand size={20} weight="medium" href={null} />
          </div>
        </Pane>
      </div>

      {/* ──────────────────────────────────────────────────────────────── */}
      <H2>2 · Mark sizes — 4×4 default, 3×3 below 18px</H2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        <Card label="128 px · hero">
          <Mark4x4 size={128} />
        </Card>
        <Card label="64 px · landing">
          <Mark4x4 size={64} />
        </Card>
        <Card label="40 px · marketing nav">
          <Mark4x4 size={40} />
        </Card>
        <Card label="28 px · default header">
          <Mark4x4 size={28} />
        </Card>
        <Card label="20 px · compact UI">
          <Mark4x4 size={20} />
        </Card>
        <Card label="16 px · favicon (3×3 fallback)">
          <Mark3x3 size={16} />
        </Card>
        <Card label="32 px · 3×3 family member">
          <Mark3x3 size={32} />
        </Card>
        <Card label="48 px · 3×3 family member">
          <Mark3x3 size={48} />
        </Card>
      </div>

      {/* ──────────────────────────────────────────────────────────────── */}
      <H2>3 · Accent cell position — pick one</H2>
      <p style={{ color: 'var(--fg-low)', fontSize: 13, marginBottom: 16, maxWidth: 640 }}>
        The single emerald cell is the entire brand signature. Its position
        carries meaning: top-left says "fresh start", off-centre says "active
        row", bottom-right says "complete". Pick one and stay consistent.
      </p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        {ACCENT_POSITIONS.map((a) => (
          <Card key={a.label} label={a.label}>
            <Logomark size={56} accent={a.pos} />
          </Card>
        ))}
      </div>

      {/* ──────────────────────────────────────────────────────────────── */}
      <H2>4 · Wordmark variations</H2>
      <p style={{ color: 'var(--fg-low)', fontSize: 13, marginBottom: 16, maxWidth: 640 }}>
        Geist set in nine permutations of weight, case, tracking and ligature
        treatment. The "emerald `up` ligature" variants underline the
        characters `up` with a thin emerald rule — turning the wordmark into
        a tiny piece of brand expression on its own.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {WORDMARK_VARIANTS.map((v) => (
          <Card key={v.id} label={`#${v.id}  ·  ${v.label}`}>
            <Wordmark {...v.props} />
          </Card>
        ))}
      </div>

      {/* ──────────────────────────────────────────────────────────────── */}
      <H2>5 · Lockup combos — mark + wordmark, several weights</H2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Pane mode="light" label="Light">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'flex-start' }}>
            <Brand size={40} weight="regular" href={null} />
            <Brand size={40} weight="medium" href={null} />
            <Brand size={40} weight="semibold" href={null} />
            <Brand size={40} weight="bold" href={null} />
          </div>
        </Pane>
        <Pane mode="dark" label="Dark">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, alignItems: 'flex-start' }}>
            <Brand size={40} weight="regular" href={null} />
            <Brand size={40} weight="medium" href={null} />
            <Brand size={40} weight="semibold" href={null} />
            <Brand size={40} weight="bold" href={null} />
          </div>
        </Pane>
      </div>

      {/* ──────────────────────────────────────────────────────────────── */}
      <H2>6 · Real-world contexts (light)</H2>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Pane mode="light" label="Nav header">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              border: '1px solid var(--border-hair)',
              borderRadius: 10,
              background: 'var(--bg-surface)',
            }}
          >
            <Brand size={26} weight="medium" href={null} />
            <span style={{ fontFamily: 'Geist Mono, monospace', fontSize: 11, color: 'var(--fg-low)' }}>
              Features · Pricing · Sign in
            </span>
          </div>
        </Pane>
        <Pane mode="light" label="App sidebar tile">
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '12px 14px',
              border: '1px solid var(--border-hair)',
              borderRadius: 10,
              background: 'var(--bg-surface)',
              width: '100%',
            }}
          >
            <Mark4x4 size={28} />
            <div>
              <div style={{ fontFamily: 'Geist', fontWeight: 600, fontSize: 13 }}>edupra</div>
              <div style={{ fontFamily: 'Geist Mono, monospace', fontSize: 10, color: 'var(--fg-low)' }}>
                SR School of Excellence
              </div>
            </div>
          </div>
        </Pane>
      </div>

      {/* ──────────────────────────────────────────────────────────────── */}
      <H2>7 · Favicon test — operating-system rendering</H2>
      <div style={{ display: 'flex', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
        <Card label="Browser tab (16 px)">
          <div style={{ background: 'var(--bg-canvas)', padding: 8, borderRadius: 6 }}>
            <Mark3x3 size={16} />
          </div>
        </Card>
        <Card label="iOS / Android tile (32 px in 64 px tile)">
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 14,
              background: 'var(--fg-high)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--bg-canvas)',
            }}
          >
            <Mark4x4 size={36} />
          </div>
        </Card>
        <Card label="Inverted on accent (rare)">
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 14,
              background: 'var(--color-accent-500)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#000',
            }}
          >
            <Mark4x4 size={36} accent={{ row: 2, col: 1 }} />
          </div>
        </Card>
      </div>

      <div style={{ marginTop: 64, paddingTop: 24, borderTop: '1px solid var(--border-hair)', fontSize: 12, color: 'var(--fg-low)', fontFamily: 'Geist Mono, monospace' }}>
        edupra brand sheet · v1 · {new Date().toISOString().slice(0, 10)}
      </div>
    </div>
  );
}
